"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangleIcon, RefreshCwIcon, WifiOffIcon } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { isOpenSearchDownError } from "./_lib/errors";

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const t = useTranslations("error");
  const commonT = useTranslations("common");

  // Handle OpenSearch down error with specific UI
  if (isOpenSearchDownError(error)) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <WifiOffIcon className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-lg">
              {t("searchUnavailable.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("searchUnavailable.message")}
            </p>

            <div className="flex flex-col gap-2">
              <Button asChild variant="default" size="sm">
                <Link href={error.retryHref}>
                  <RefreshCwIcon className="mr-2 h-4 w-4" />
                  {t("retry")}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default error UI
  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-lg">{t("title")}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            {error.message || t("generic")}
          </p>

          <Button onClick={reset} variant="outline" size="sm">
            <RefreshCwIcon className="mr-2 h-4 w-4" />
            {t("retry")}
          </Button>

          <p className="text-xs text-muted-foreground">
            {t.rich("contactSupport", {
              link: (chunks) => (
                <a
                  key="support"
                  href={commonT("supportUrl")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
